import React from "react";
import Icon from "@mdi/react";
import {
  mdiWarehouse,
  mdiMapMarker,
  mdiPackageVariant,
  mdiClose,
  mdiInformationOutline,
  mdiScriptTextPlayOutline,
  mdiTruckDeliveryOutline,
  mdiFileDocumentMultipleOutline,
} from "@mdi/js";
import { FileText, ImageIcon, Download } from "lucide-react";
import {
  ReturnableDCOutwardModel,
  WarehouseOutwardStatus,
} from "../../model/warehouse_outward_model";
import SummaryItem from "./SummaryItem";

const getIcon = (type: string) => {
  if (type.includes("pdf")) return <FileText size={16} />;
  if (type.includes("image")) return <ImageIcon size={16} />;
  return <FileText size={16} />;
};

const formatAddress = (addr?: any) => {
  if (!addr) return "-";
  return [
    addr.line1,
    addr.line2,
    addr.line3,
    addr.city,
    addr.state,
    addr.country,
    addr.pincode,
  ]
    .filter(Boolean)
    .join(", ");
};

// "Ready To Dispatch" is the post-Ambill state; for a reverse pickup nothing
// dispatches, so surface it as "Docs Generated".
const displayStatus = (status?: string) =>
  status === WarehouseOutwardStatus.READY_TO_DISPATCH
    ? "Docs Generated"
    : (status ?? "-");

type Props = {
  open: boolean;
  onClose: () => void;
  outward: ReturnableDCOutwardModel;
};

const ReturnableDCView: React.FC<Props> = ({ open, onClose, outward }) => {
  if (!open) return null;

  const {
    id: stoNo,
    status,
    to_warehouse_name,
    source_order_type,
    linked_order_ids = [],
    customer_name,
    billing_address,
    shipping_address,
    expected_date,
    dc_attachment,
    dc_ambill_attachment,
    eway_attachment,
    invoice_attachment,
    purpose,
    description,
    items = [],
  } = outward;

  const totalValue =
    items.reduce((sum, i) => sum + (i.itemPrice || 0) * i.requestedQty, 0) || 0;

  return (
    <div
      id="warehouse-stock-transfer-returnable-dc-view-modal"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-40 overflow-y-auto p-4 sm:p-8"
    >
      <div className="relative w-full sm:max-w-2xl max-h-[85vh] bg-white shadow-lg flex flex-col rounded-lg wh-modal-xs">
        <div className="flex-shrink-0 flex items-start justify-between border-b px-6 py-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">{stoNo}</h2>
            <span className="rounded-full border px-2.5 py-0.5 text-xs font-medium">
              Reverse DC
            </span>
            <span className="rounded-full bg-blue-100 text-blue-700 px-2.5 py-0.5 text-xs font-medium">
              {displayStatus(status)}
            </span>
          </div>
          <button
            id="warehouse-stock-transfer-returnable-dc-view-modal-close-btn"
            onClick={onClose}
            className="rounded-md p-1 hover:bg-gray-100"
          >
            <Icon path={mdiClose} size={0.9} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <SummaryItem
              icon={mdiFileDocumentMultipleOutline}
              label={`Against (${source_order_type})`}
              value={linked_order_ids.join(", ")}
              color="text-gray-600"
            />
            <SummaryItem
              icon={mdiWarehouse}
              label="To Warehouse (Kazam)"
              value={to_warehouse_name}
              color="text-blue-600"
            />
            <SummaryItem
              icon={mdiMapMarker}
              label="Pickup From"
              value={customer_name}
              color="text-green-600"
            />
            <SummaryItem
              icon={mdiMapMarker}
              label="Pickup Address"
              value={formatAddress(shipping_address)}
              color="text-green-600"
            />
            <SummaryItem
              icon={mdiMapMarker}
              label="Billing Address"
              value={formatAddress(billing_address)}
              color="text-green-600"
            />
            <SummaryItem
              icon={mdiPackageVariant}
              label="Total Value"
              value={`Rs. ${totalValue.toLocaleString()}`}
              color="text-orange-600"
            />
            <SummaryItem
              icon={mdiInformationOutline}
              label="Purpose"
              value={purpose}
              color="text-gray-600"
            />
            <SummaryItem
              icon={mdiScriptTextPlayOutline}
              label="Description"
              value={description}
              color="text-gray-500"
            />
            {outward.shipping_details && (
              <>
                <SummaryItem
                  icon={mdiTruckDeliveryOutline}
                  label="Logistic Partner"
                  value={outward.shipping_details.logistic_partner}
                  color="text-indigo-600"
                />
                <SummaryItem
                  icon={mdiTruckDeliveryOutline}
                  label="Tracking ID"
                  value={outward.shipping_details.tracking_id}
                  color="text-indigo-600"
                />
              </>
            )}
          </div>

          {[
            dc_attachment,
            eway_attachment,
            invoice_attachment,
            dc_ambill_attachment,
          ]
            .filter(Boolean)
            .map((attachment: any) => (
              <div
                key={attachment.id || attachment.url}
                id={`warehouse-stock-transfer-returnable-dc-view-modal-attachment-${attachment.id || attachment.url}`}
                className="border rounded-lg bg-gray-50"
              >
                <div className="flex items-center justify-between text-sm bg-white rounded px-3 py-2">
                  <div className="flex items-center gap-2 truncate">
                    {getIcon(attachment.type)}
                    <span className="truncate">{attachment.filename}</span>
                  </div>
                  <a
                    id={`warehouse-stock-transfer-returnable-dc-view-modal-attachment-${attachment.id || attachment.url}-view-link`}
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline flex items-center gap-1 text-xs"
                  >
                    <Download size={14} />
                    View
                  </a>
                </div>
              </div>
            ))}

          <div className="space-y-4">
            {items.map((item, idx) => (
              <div
                key={idx}
                id={`warehouse-stock-transfer-returnable-dc-view-modal-item-row-${item.inventoryItemCode ?? idx}`}
                className="rounded-lg border p-4 space-y-3"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{item.itemName}</p>
                    <p className="text-sm text-gray-500">
                      {item.inventoryItemCode}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Qty: {item.requestedQty}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Unit Price: ₹{item.itemPrice?.toLocaleString()}
                    </p>
                  </div>
                  <p className="font-semibold">
                    Rs.{" "}
                    {(
                      item.requestedQty * (item.itemPrice || 0)
                    ).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnableDCView;
